/**
 * Custom Hook: useUrlState
 *
 * Provides bidirectional synchronization between React component state and URL parameters.
 * Enables shareable URLs and browser navigation support (back/forward buttons).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  UrlState,
  encodeStateToUrl,
  decodeUrlToState,
  getCurrentUrlParams,
  updateBrowserUrl,
  validateStateName,
} from 'utils/urlStateUtils';
import debounce from 'util/debounce';
import { SearchMode } from 'interfaces/SearchTypes';

interface UseUrlStateOptions {
  stateMetadata: { [key: string]: any };
  debounceMs?: number;
  replaceInitialState?: boolean;
}

interface UseUrlStateReturn {
  searchQuery: string;
  selectedState: string | null;
  mode: SearchMode;
  setSearchQuery: (query: string) => void;
  setSelectedState: (state: string | null) => void;
  setMode: (mode: SearchMode) => void;
  updateUrl: (newState: Partial<UrlState>, replace?: boolean) => void;
}

/**
 * Hook that manages component state synchronized with URL parameters
 */
export const useUrlState = ({
  stateMetadata,
  debounceMs = 300,
  replaceInitialState = true,
}: UseUrlStateOptions): UseUrlStateReturn => {
  // Check if data is loaded
  const isDataLoaded = Object.keys(stateMetadata).length > 0;

  // Initialize state from URL or defaults
  const getInitialState = useCallback((): UrlState => {
    const urlParams = getCurrentUrlParams();
    const urlState = decodeUrlToState(urlParams);

    // Only validate selected state if data is loaded
    let validatedState = null;
    if (urlState.selectedState && isDataLoaded) {
      validatedState = validateStateName(urlState.selectedState, stateMetadata);
    } else if (urlState.selectedState && !isDataLoaded) {
      // If data isn't loaded yet, preserve the state name from URL for later validation
      validatedState = urlState.selectedState;
    }

    return {
      searchQuery: urlState.searchQuery || '',
      selectedState: validatedState,
      mode: urlState.mode || 'search',
    };
  }, [stateMetadata, isDataLoaded]);

  const [state, setState] = useState<UrlState>(getInitialState);
  const isInitialMount = useRef(true);
  const hasProcessedInitialUrl = useRef(false);

  // Debounced URL update function
  const debouncedUpdateUrl = useMemo(
    () =>
      debounce((newState: UrlState, replace: boolean = false) => {
        const params = encodeStateToUrl(newState);

        // Skip writes that would duplicate the current URL: the post-load sync,
        // a popstate we are echoing back, or an updateUrl that already wrote.
        if (params.toString() === getCurrentUrlParams().toString()) return;

        updateBrowserUrl(params, replace);
      }, debounceMs),
    [debounceMs],
  );

  // Update URL when state changes (but not on initial mount to avoid duplicate history entry)
  useEffect(() => {
    // Don't update URL until data is loaded
    if (!isDataLoaded) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Initial URL processing is handled in the data loading effect
    }

    // Update URL for subsequent state changes
    debouncedUpdateUrl(state, false);
  }, [state, debouncedUpdateUrl, isDataLoaded]);

  // Process URL state when data becomes available
  useEffect(() => {
    if (isDataLoaded && !hasProcessedInitialUrl.current) {
      hasProcessedInitialUrl.current = true;

      const urlParams = getCurrentUrlParams();
      const urlState = decodeUrlToState(urlParams);

      // Now that data is loaded, validate and update state
      let validatedState = null;
      if (urlState.selectedState) {
        validatedState = validateStateName(
          urlState.selectedState,
          stateMetadata,
        );
      }

      const processedState = {
        searchQuery: urlState.searchQuery || '',
        selectedState: validatedState,
        mode: urlState.mode || 'search',
      };

      setState(processedState);

      // Update URL to clean up any invalid state names
      if (replaceInitialState) {
        const params = encodeStateToUrl(processedState);
        updateBrowserUrl(params, true);
      }
    }
  }, [isDataLoaded, stateMetadata, replaceInitialState]);

  // Listen for browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopstate = () => {
      // Only process popstate if data is loaded
      if (!isDataLoaded) return;

      const urlParams = getCurrentUrlParams();
      const urlState = decodeUrlToState(urlParams);

      // Validate selected state
      let validatedState = null;
      if (urlState.selectedState) {
        validatedState = validateStateName(
          urlState.selectedState,
          stateMetadata,
        );
      }

      // Update component state to match URL
      setState({
        searchQuery: urlState.searchQuery || '',
        selectedState: validatedState,
        mode: urlState.mode || 'search',
      });
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [stateMetadata, isDataLoaded]);

  // State setter functions
  const setSearchQuery = useCallback((query: string) => {
    setState((prev) => ({ ...prev, searchQuery: query }));
  }, []);

  const setSelectedState = useCallback((selectedState: string | null) => {
    setState((prev) => ({ ...prev, selectedState }));
  }, []);

  const setMode = useCallback((mode: SearchMode) => {
    setState((prev) => ({ ...prev, mode }));
  }, []);

  // Manual URL update function for complex state changes
  const updateUrl = useCallback(
    (newState: Partial<UrlState>, replace: boolean = false) => {
      const updatedState = { ...state, ...newState };
      setState(updatedState);

      // Force immediate URL update (bypass debouncing)
      const params = encodeStateToUrl(updatedState);
      updateBrowserUrl(params, replace);
    },
    [state],
  );

  return {
    searchQuery: state.searchQuery,
    selectedState: state.selectedState,
    mode: state.mode,
    setSearchQuery,
    setSelectedState,
    setMode,
    updateUrl,
  };
};
